import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LockKey, ShieldCheck, GraduationCap, Sparkle, CheckCircle } from "@phosphor-icons/react";

const rules = [
  { test: (p) => p.length >= 8, label: "Ao menos 8 caracteres" },
  { test: (p) => /[A-Z]/.test(p), label: "1 letra maiúscula" },
  { test: (p) => /[a-z]/.test(p), label: "1 letra minúscula" },
  { test: (p) => /\d/.test(p), label: "1 número" },
];

export default function ChangePasswordPage() {
  const { refresh, logout } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const passed = rules.map(r => r.test(next));
  const strongEnough = passed.every(Boolean);
  const matches = next && next === confirm;

  async function submit(e) {
    e.preventDefault();
    if (!strongEnough) { toast.error("A nova senha não atende os requisitos"); return; }
    if (!matches) { toast.error("As senhas não coincidem"); return; }
    if (current === next) { toast.error("A nova senha deve ser diferente da atual"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Senha atualizada com sucesso!");
      await refresh();
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao trocar senha");
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen bg-[#F7F3EE] relative overflow-hidden flex items-center justify-center px-4 py-10">
      <div className="blob w-[500px] h-[500px] bg-[#FFB86B]/40 -top-40 -left-40" />
      <div className="blob w-[400px] h-[400px] bg-[#1E5FFF]/20 bottom-0 right-0" />

      <div className="relative z-10 w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-11 h-11 rounded-2xl gradient-primary flex items-center justify-center shadow-lg">
            <GraduationCap size={22} color="white" weight="duotone" />
          </div>
          <div>
            <div className="font-heading font-black text-lg leading-none">Caminhos</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">APP</div>
          </div>
        </div>

        <div className="card-elevated p-8 sm:p-10">
          <div className="pill-badge bg-[#E0EBFF] text-[#1E5FFF] mb-4">
            <Sparkle size={12} weight="fill" /> Primeiro acesso
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black text-[#12143A] leading-tight">
            Crie sua senha definitiva
          </h1>
          <p className="text-sm text-zinc-500 mt-2 leading-relaxed">
            Você entrou com uma senha temporária. Para sua segurança, defina agora uma senha pessoal antes de continuar.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Senha atual (temporária)</Label>
              <Input data-testid="cp-current" type="password" value={current} required
                     onChange={e => setCurrent(e.target.value)}
                     className="mt-2 h-12 rounded-2xl border-zinc-200 bg-white focus:border-[#1E5FFF] focus-visible:ring-[#1E5FFF]/20" />
            </div>
            <div>
              <Label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Nova senha</Label>
              <Input data-testid="cp-new" type="password" value={next} required
                     onChange={e => setNext(e.target.value)}
                     className="mt-2 h-12 rounded-2xl border-zinc-200 bg-white focus:border-[#1E5FFF] focus-visible:ring-[#1E5FFF]/20" />
              <ul className="mt-3 grid grid-cols-2 gap-1.5">
                {rules.map((r, i) => (
                  <li key={r.label} data-testid={`cp-rule-${i}`}
                      className={`text-xs flex items-center gap-1.5 ${passed[i] ? "text-[#12B76A]" : "text-zinc-400"}`}>
                    <CheckCircle size={13} weight={passed[i] ? "fill" : "regular"} />
                    {r.label}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <Label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Confirme a nova senha</Label>
              <Input data-testid="cp-confirm" type="password" value={confirm} required
                     onChange={e => setConfirm(e.target.value)}
                     className="mt-2 h-12 rounded-2xl border-zinc-200 bg-white focus:border-[#1E5FFF] focus-visible:ring-[#1E5FFF]/20" />
              {confirm && !matches && <p className="text-xs text-[#FF3D71] mt-1">As senhas não coincidem</p>}
            </div>

            <Button data-testid="cp-submit" type="submit" disabled={loading || !strongEnough || !matches}
              className="w-full h-13 h-12 rounded-2xl gradient-primary hover:opacity-90 text-white font-bold text-base shadow-[0_10px_30px_rgba(30,95,255,0.35)]">
              <ShieldCheck size={16} className="mr-2" weight="fill" /> {loading ? "Salvando…" : "Salvar nova senha"}
            </Button>

            <button type="button" onClick={logout} data-testid="cp-logout"
                    className="w-full text-sm text-zinc-500 hover:text-[#1E5FFF] font-semibold py-2">
              Sair e voltar para o login
            </button>
          </form>
        </div>

        <p className="text-center text-[11px] text-zinc-400 mt-6 flex items-center justify-center gap-1">
          <LockKey size={11} weight="fill" /> Sua senha é criptografada e nunca compartilhada.
        </p>
      </div>
    </div>
  );
}
